import { useState } from "react";
import { Toaster } from "sonner";
import { BadgeCheck, Bell, Briefcase, ChevronRight, GraduationCap, LayoutDashboard, Menu, MoreHorizontal, Search, ShieldCheck, Sparkles, Star, Users, Workflow } from "lucide-react";
import "@/App.css";

import DashboardPage from "./pages/DashboardPage";
import OpportunitiesPage from "./pages/OpportunitiesPage";
import QualificationsPage from "./pages/QualificationsPage";
import LearningPage from "./pages/LearningPage";
import TalentPage from "./pages/TalentPage";
import ReputationPage from "./pages/ReputationPage";
import SourcingPage, { CreateRfqModal } from "./pages/SourcingPage";
import { PROFILE } from "./data/kabelul";

const NAV = [
  [LayoutDashboard, "Dashboard", "dashboard"],
  [Briefcase, "Opportunities", "opportunities"],
  [Users, "Talent", "talent"],
  [GraduationCap, "Learning", "learning"],
  [BadgeCheck, "Qualifications", "qualifications"],
  [Workflow, "Sourcing", "sourcing"],
  [Star, "Reputation", "reputation"],
];

function Sidebar({ page, setPage }) {
  return (
    <aside className="sidebar k-sidebar">
      <div className="brand">
        <div className="brand-mark k-brand-mark"><span>K</span></div>
        <div>
          <b>KABELUL</b>
          <small>Learn · Qualify · Work · Grow</small>
        </div>
      </div>

      <div className="workspace k-workspace">
        <img src={PROFILE.photo} alt={PROFILE.name} className="workspace-avatar-img" />
        <div>
          <b>{PROFILE.name}</b>
          <small>{PROFILE.tier} · Tier {PROFILE.tierLevel}/{PROFILE.tierMax}</small>
        </div>
        <ChevronRight size={15} />
      </div>

      <nav>
        <small className="nav-label">ECOSYSTEM</small>
        {NAV.slice(0, 5).map(([Icon, label, key]) => (
          <button
            key={key}
            data-testid={`nav-${key}`}
            className={page === key ? "nav-item active" : "nav-item"}
            onClick={() => setPage(key)}
          >
            <Icon size={17} />
            <span>{label}</span>
            {key === "opportunities" && <em>12</em>}
          </button>
        ))}
        <small className="nav-label space">FOR BUSINESSES</small>
        {NAV.slice(5).map(([Icon, label, key]) => (
          <button
            key={key}
            data-testid={`nav-${key}`}
            className={page === key ? "nav-item active" : "nav-item"}
            onClick={() => setPage(key)}
          >
            <Icon size={17} />
            <span>{label}</span>
            {key === "sourcing" && <em>4</em>}
          </button>
        ))}
      </nav>

      <div className="sidebar-foot">
        <div className="ai-pulse">
          <Sparkles size={16} />
          <div>
            <b>KABELUL AI online</b>
            <small>Matching skills to opportunities</small>
          </div>
          <i />
        </div>
        <div className="profile">
          <img src={PROFILE.photo} alt="" className="profile-avatar-img" />
          <div>
            <b>{PROFILE.name}</b>
            <small>{PROFILE.location}</small>
          </div>
          <MoreHorizontal size={17} />
        </div>
      </div>
    </aside>
  );
}

function Topbar({ title, page, setShowCreate }) {
  return (
    <header className="topbar">
      <div className="mobile-brand">
        <div className="brand-mark k-brand-mark"><span>K</span></div>
        <b>KABELUL</b>
      </div>
      <div className="crumb">
        <span>KABELUL</span>
        <ChevronRight size={14} />
        <b>{title}</b>
      </div>
      <div className="top-actions">
        <div className="search">
          <Search size={16} />
          <input data-testid="global-search-input" placeholder="Search skills, opportunities, vendors..." />
        </div>
        <button data-testid="notifications-button" className="icon-button">
          <Bell size={18} />
          <i />
        </button>
        {page === "sourcing" && (
          <button data-testid="create-rfq-header-button" className="primary-button" onClick={() => setShowCreate(true)}>
            <Workflow size={16} /> Create RFQ
          </button>
        )}
      </div>
    </header>
  );
}

const TITLES = {
  dashboard: "Dashboard",
  opportunities: "Opportunities",
  talent: "Talent",
  learning: "Learning",
  qualifications: "Qualifications",
  sourcing: "Sourcing",
  reputation: "Reputation",
};

export default function App() {
  const [page, setPage] = useState("dashboard");
  const [showCreate, setShowCreate] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigate = (p) => {
    setPage(p);
    setMobileOpen(false);
  };

  return (
    <>
      <div className="app-shell">
        <button data-testid="mobile-menu-button" className="mobile-menu" onClick={() => setMobileOpen(!mobileOpen)}>
          <Menu size={20} />
        </button>
        <div className={mobileOpen ? "sidebar mobile-visible" : "sidebar-wrap"}>
          <Sidebar page={page} setPage={navigate} />
        </div>
        <main>
          <Topbar title={TITLES[page]} page={page} setShowCreate={setShowCreate} />
          {page === "dashboard" && <DashboardPage setPage={navigate} />}
          {page === "opportunities" && <OpportunitiesPage setPage={navigate} />}
          {page === "talent" && <TalentPage setPage={navigate} />}
          {page === "learning" && <LearningPage setPage={navigate} />}
          {page === "qualifications" && <QualificationsPage setPage={navigate} />}
          {page === "sourcing" && <SourcingPage setShowCreate={setShowCreate} />}
          {page === "reputation" && <ReputationPage setPage={navigate} />}
        </main>
      </div>
      {showCreate && (
        <CreateRfqModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); setPage("sourcing"); }}
        />
      )}
      <Toaster position="bottom-right" />
    </>
  );
}
